import { useState, useCallback, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import api from '../api/axios';
import ResultsTable from './ResultsTable';
import { useAuth } from '../context/AuthContext';

const C = { green: '#3cb74e', black: '#111111', white: '#ffffff', red: '#ef4444', amber: '#f59e0b', gray: '#64748b', lightBg: '#f8fdf8', border: '#e2e8f0' };

export default function BulkVerifier() {
  const [results, setResults]         = useState([]);
  const [summary, setSummary]         = useState(null);
  const [batches, setBatches]         = useState([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');
  const [timeTaken, setTimeTaken]     = useState(null);
  const [duplicateCount, setDuplicate]= useState(0);
  const [progress, setProgress]       = useState(0);
  const [progressLabel, setProgLabel] = useState('');
  const [currentJobId, setCurrentJobId] = useState(null);   // ← track jobId
  const { updateUser }                = useAuth();
  const pollRef                       = useRef(null);

  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;
    setError(''); setResults([]); setSummary(null); setBatches([]);
    setTimeTaken(null); setProgress(0); setProgLabel('Uploading...');
    setCurrentJobId(null); setLoading(true);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await api.post('/email/bulk', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const { jobId, total, duplicateCount: dups } = res.data;
      setDuplicate(dups || 0);
      setCurrentJobId(jobId);
      setProgLabel(`Processing 0 / ${total} emails…`);

      await new Promise((resolve, reject) => {
        pollRef.current = setInterval(async () => {
          try {
            const poll = await api.get(`/email/bulk/status/${jobId}`);
            const job = poll.data;
            const pct = job.total > 0 ? Math.round((job.processed / job.total) * 100) : 0;
            setProgress(pct);
            setProgLabel(`Processing ${job.processed} / ${job.total} emails…`);

            if (job.status === 'completed') {
              clearInterval(pollRef.current);
              setResults(job.results || []);
              setSummary(job.summary);
              setBatches(job.batches || []);
              setTimeTaken(job.timeTaken);
              if (job.usage) updateUser(job.usage);
              resolve();
            } else if (job.status === 'failed') {
              clearInterval(pollRef.current);
              reject(new Error('Job failed on server'));
            }
          } catch (e) { clearInterval(pollRef.current); reject(e); }
        }, 2000);
      });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Bulk verification failed');
    } finally { setLoading(false); setProgLabel(''); }
  }, [updateUser]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop, accept: { 'text/csv': ['.csv'] }, maxFiles: 1, disabled: loading,
  });

  // ── FIX: pass job_id so only THIS batch is exported ──
  const downloadCSV = async (statusFilter = null, label = 'results') => {
    if (!currentJobId) { alert('No job to export'); return; }
    try {
      const params = new URLSearchParams({ job_id: currentJobId });
      if (statusFilter) params.set('status', statusFilter);
      const res = await api.get(`/export/csv?${params}`, { responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([res.data]));
      a.download = `job${currentJobId}_${label}.csv`;
      a.click();
    } catch { alert('Export failed'); }
  };

  const speed = timeTaken && results.length ? Math.round((results.length / timeTaken) * 60) : null;

  return (
    <div>
      {/* Drop Zone */}
      <div {...getRootProps()} style={{ ...s.dropzone, ...(isDragActive ? s.dropActive : {}), ...(loading ? s.dropLoading : {}) }}>
        <input {...getInputProps()} />
        {loading ? (
          <div style={s.loadingBox}>
            <svg width="80" height="80" viewBox="0 0 80 80">
              <circle cx="40" cy="40" r="34" fill="none" stroke="#e2e8f0" strokeWidth="7"/>
              <circle cx="40" cy="40" r="34" fill="none" stroke={C.green} strokeWidth="7"
                strokeDasharray={`${2 * Math.PI * 34 * progress / 100} ${2 * Math.PI * 34}`}
                strokeLinecap="round" transform="rotate(-90 40 40)"
                style={{ transition: 'stroke-dasharray .4s ease' }}
              />
              <text x="40" y="44" textAnchor="middle" fontSize="14" fontWeight="700" fill={C.green}>{progress}%</text>
            </svg>
            <p style={s.loadingText}>{progressLabel}</p>
            <p style={s.loadingHint}>Large files may take a few minutes</p>
          </div>
        ) : (
          <>
            <div style={s.dropIcon}>📂</div>
            <p style={s.dropTitle}>{isDragActive ? 'Drop CSV here...' : 'Drag & drop CSV file here'}</p>
            <p style={s.dropHint}>or click to browse · One email per row · Max 50,000 emails</p>
          </>
        )}
      </div>

      {error && <div style={s.errorBox}>⚠️ {error}</div>}

      {/* Batch Log */}
      {batches.length > 0 && (
        <div style={s.batchSection}>
          <h3 style={s.batchTitle}>📋 Batch Processing Log</h3>

          {summary && (
            <div style={s.summaryRow}>
              {[
                { label: 'Total',              value: results.length,      color: C.black },
                { label: 'Valid',              value: summary.valid || 0,  color: C.green },
                { label: 'Invalid',            value: summary.invalid || 0,color: C.red },
                { label: 'Risky',              value: summary.risky || 0,  color: C.amber },
                { label: 'Unknown',            value: summary.unknown || 0,color: C.gray },
                { label: 'Duplicates Removed', value: duplicateCount,      color: '#8b5cf6' },
              ].map(({ label, value, color }) => (
                <div key={label} style={s.summaryCard}>
                  <p style={{ ...s.summaryNum, color }}>{value}</p>
                  <p style={s.summaryLabel}>{label}</p>
                </div>
              ))}
            </div>
          )}

          {timeTaken && (
            <div style={s.speedRow}>
              <span style={s.speedItem}>⏱️ Time: <strong>{timeTaken < 60 ? `${timeTaken}s` : `${Math.floor(timeTaken/60)}m ${timeTaken%60}s`}</strong></span>
              <span style={s.speedItem}>⚡ Speed: <strong>{speed} emails/min</strong></span>
              <span style={s.speedItem}>📦 Batches: <strong>{batches.length}</strong></span>
              {duplicateCount > 0 && <span style={s.speedItem}>🔁 Duplicates removed: <strong>{duplicateCount}</strong></span>}
            </div>
          )}

          <div style={s.batchLog}>
            {batches.map((batch) => (
              <div key={batch.batchNumber} style={s.batchItem}>
                <span style={s.batchCheck}>✅</span>
                <span style={s.batchLabel}>Batch {batch.batchNumber}</span>
                <span style={s.batchCount}>{batch.total} emails</span>
                <div style={s.batchCounts}>
                  {batch.counts.valid   > 0 && <span style={{ ...s.batchBadge, background: '#dcfce7', color: C.green }}>✓ {batch.counts.valid} valid</span>}
                  {batch.counts.invalid > 0 && <span style={{ ...s.batchBadge, background: '#fee2e2', color: C.red }}>✗ {batch.counts.invalid} invalid</span>}
                  {batch.counts.risky   > 0 && <span style={{ ...s.batchBadge, background: '#fef3c7', color: C.amber }}>⚠ {batch.counts.risky} risky</span>}
                  {batch.counts.unknown > 0 && <span style={{ ...s.batchBadge, background: '#f1f5f9', color: C.gray }}>? {batch.counts.unknown} unknown</span>}
                </div>
                <span style={s.batchDone}>Done</span>
              </div>
            ))}
          </div>

          {/* ── Export buttons — all scoped to currentJobId ── */}
          <div style={s.actionRow}>
            <div style={s.actionLeft}>
              <p style={s.exportNote}>📎 Job #{currentJobId} · {results.length} emails</p>
            </div>
            <div style={s.actionBtns}>
              <button style={s.exportAllBtn}   onClick={() => downloadCSV(null,      'all')}>⬇️ Export All ({results.length})</button>
              <button style={s.exportValidBtn} onClick={() => downloadCSV('valid',   'valid')}>✅ Valid Only ({summary?.valid || 0})</button>
              <button style={s.exportInvalidBtn} onClick={() => downloadCSV('invalid','invalid')}>❌ Invalid Only ({summary?.invalid || 0})</button>
            </div>
          </div>
        </div>
      )}

      {results.length > 0 && <ResultsTable results={results} jobId={currentJobId} />}
    </div>
  );
}

const s = {
  dropzone:     { border: `2.5px dashed ${C.border}`, borderRadius: 14, padding: 44, textAlign: 'center', cursor: 'pointer', background: C.lightBg, transition: 'all 0.2s', marginBottom: 20 },
  dropActive:   { borderColor: C.green, background: '#f0fdf4' },
  dropLoading:  { cursor: 'not-allowed', opacity: 0.9 },
  loadingBox:   { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 },
  loadingText:  { color: C.black, fontWeight: 700, fontSize: 15, margin: 0 },
  loadingHint:  { color: C.gray, fontSize: 13, margin: 0 },
  dropIcon:     { fontSize: 42, marginBottom: 10 },
  dropTitle:    { color: C.black, fontWeight: 700, fontSize: 16, marginBottom: 6 },
  dropHint:     { color: C.gray, fontSize: 13 },
  errorBox:     { background: '#fef2f2', border: `1.5px solid ${C.red}`, color: '#991b1b', padding: '12px 16px', borderRadius: 8, fontSize: 14, marginBottom: 16 },
  batchSection: { background: C.white, borderRadius: 14, border: `1.5px solid ${C.border}`, overflow: 'hidden', marginBottom: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' },
  batchTitle:   { color: C.black, fontSize: 16, fontWeight: 700, padding: '16px 20px', borderBottom: `1px solid ${C.border}`, margin: 0, background: C.lightBg },
  summaryRow:   { display: 'flex', gap: 12, padding: 16, flexWrap: 'wrap', borderBottom: `1px solid ${C.border}` },
  summaryCard:  { flex: 1, minWidth: 80, textAlign: 'center', padding: '10px 8px', background: '#f8fdf8', borderRadius: 8, border: `1px solid ${C.border}` },
  summaryNum:   { fontSize: 24, fontWeight: 800, margin: 0 },
  summaryLabel: { color: C.gray, fontSize: 11, margin: '3px 0 0' },
  speedRow:     { display: 'flex', gap: 20, padding: '10px 20px', background: '#f8fdf8', borderBottom: `1px solid ${C.border}`, flexWrap: 'wrap' },
  speedItem:    { color: C.gray, fontSize: 13 },
  batchLog:     { padding: '8px 0', maxHeight: 320, overflowY: 'auto' },
  batchItem:    { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 20px', borderBottom: `1px solid ${C.border}` },
  batchCheck:   { fontSize: 16 },
  batchLabel:   { color: C.black, fontWeight: 700, fontSize: 14, minWidth: 70 },
  batchCount:   { color: C.gray, fontSize: 13, minWidth: 70 },
  batchCounts:  { display: 'flex', gap: 6, flex: 1, flexWrap: 'wrap' },
  batchBadge:   { padding: '2px 8px', borderRadius: 12, fontSize: 12, fontWeight: 600 },
  batchDone:    { color: C.green, fontSize: 12, fontWeight: 700, background: '#dcfce7', padding: '2px 10px', borderRadius: 12 },
  actionRow:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', background: '#f8fdf8', flexWrap: 'wrap', gap: 10 },
  actionLeft:   {},
  exportNote:   { color: C.gray, fontSize: 12, margin: 0 },
  actionBtns:   { display: 'flex', gap: 8, flexWrap: 'wrap' },
  exportAllBtn:    { padding: '9px 16px', background: C.black, color: C.white, border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 12 },
  exportValidBtn:  { padding: '9px 16px', background: C.green, color: C.white, border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 12 },
  exportInvalidBtn:{ padding: '9px 16px', background: C.red,   color: C.white, border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 12 },
};
